import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuthStore, type User } from '@/store/authStore';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api';
import toast from 'react-hot-toast';
import PasswordInput from '@/components/PasswordInput';

export default function ProfilePage() {
  const user = useAuthStore((s) => s.user);
  const setAuth = useAuthStore((s) => s.setAuth);
  const accessToken = useAuthStore((s) => s.accessToken);
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const queryClient = useQueryClient();

  const [fullName, setFullName] = useState(user?.full_name ?? '');
  const [phone, setPhone] = useState('');
  const [showPwForm, setShowPwForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');

  // Fetch fresh profile
  useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const { data } = await api.get('/users/me');
      if (data.user?.phone) setPhone(data.user.phone);
      return data;
    },
  });

  // Update profile mutation
  const updateMutation = useMutation({
    mutationFn: async (body: Record<string, string>) => {
      const { data } = await api.put('/users/me', body);
      return data;
    },
    onSuccess: (data) => {
      // Update Zustand store with fresh user data
      if (data.user && accessToken && refreshToken) {
        setAuth(data.user as User, accessToken, refreshToken);
      }
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      toast.success('Profilo aggiornato');
    },
    onError: () => toast.error('Errore nell\'aggiornamento'),
  });

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const body: Record<string, string> = { full_name: fullName };
    if (phone) body.phone = phone;
    updateMutation.mutate(body);
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error('La password deve avere almeno 8 caratteri');
      return;
    }
    updateMutation.mutate({
      current_password: currentPassword,
      password: newPassword,
    });
    setCurrentPassword('');
    setNewPassword('');
    setShowPwForm(false);
  };

  return (
    <>
      <Helmet>
        <title>Profilo — React-City</title>
      </Helmet>

      <div className="space-y-6 animate-in max-w-2xl">
        <div>
          <h1 className="text-2xl font-bold text-heading">Profilo</h1>
          <p className="text-muted text-sm mt-1">
            Gestisci le tue informazioni personali e le impostazioni
          </p>
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold mb-4 text-heading">Informazioni personali</h2>

          <form onSubmit={handleProfileSubmit} className="space-y-4">
            <div>
              <label htmlFor="full_name" className="label">Nome completo</label>
              <input
                id="full_name"
                type="text"
                className="input"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
              />
            </div>

            <div>
              <label htmlFor="phone" className="label">Telefono</label>
              <input
                id="phone"
                type="tel"
                className="input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+39 333 1234567"
              />
            </div>

            <div>
              <label htmlFor="email" className="label">Email</label>
              <input
                id="email"
                type="email"
                className="input bg-surface-50 dark:bg-surface-800/50"
                defaultValue={user?.email ?? ''}
                disabled
              />
              <p className="text-xs text-muted mt-1">L'email non può essere modificata</p>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                className="btn-primary"
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending ? 'Salvataggio...' : 'Salva modifiche'}
              </button>
            </div>
          </form>
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold mb-4 text-heading">Sicurezza</h2>
          {!showPwForm ? (
            <button onClick={() => setShowPwForm(true)} className="btn-secondary">
              Cambia password
            </button>
          ) : (
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <label htmlFor="current_password" className="label">Password attuale</label>
                <PasswordInput
                  id="current_password"
                  className="input"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </div>
              <div>
                <label htmlFor="new_password" className="label">Nuova password</label>
                <PasswordInput
                  id="new_password"
                  className="input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
              </div>
              <div className="flex gap-3 justify-end">
                <button type="button" onClick={() => setShowPwForm(false)} className="btn-secondary">Annulla</button>
                <button type="submit" className="btn-primary" disabled={updateMutation.isPending}>
                  Aggiorna password
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="card border-red-200 dark:border-red-500/30">
          <h2 className="text-lg font-semibold text-red-500 dark:text-red-400 mb-4">Zona pericolosa</h2>
          <p className="text-sm text-muted mb-4">
            Questa azione è irreversibile e rimuoverà tutti i tuoi dati.
          </p>
          <button className="btn-danger">Elimina account</button>
        </div>
      </div>
    </>
  );
}
